package link.erpango.quip.features.myspareparts

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import link.erpango.quip.data.remote.DResponse
import link.erpango.quip.data.remote.repository.catalog.CatalogResponseDto
import link.erpango.quip.data.remote.repository.catalog.ICatalogRepository
import link.erpango.quip.domain.entities.DCompatibleVehicle
import link.erpango.quip.domain.entities.DMySparePart
import link.erpango.quip.domain.entities.DSparePartCatalog
import link.erpango.quip.domain.entities.DVehicleBrand
import link.erpango.quip.domain.entities.DVehicleModel
import link.erpango.quip.domain.usecases.IMySparePartsUseCase

/**
 * Estado de UI para la pantalla de creación/edición de un repuesto personal ("Mis repuestos").
 */
data class MySparePartEditUiState(
    val partId: Long? = null,
    /** True mientras se carga el repuesto en modo edición. */
    val isLoading: Boolean = false,
    /** Error al cargar el repuesto (modo edición). */
    val loadError: String? = null,
    val categoryId: String? = null,
    val sparePartTypeId: String? = null,
    val brand: String = "",
    val model: String = "",
    val reference: String = "",
    val durabilityDays: String = "",
    val additionalInfo: String = "",
    /** Vehículos compatibles seleccionados (marca + modelo opcional). */
    val selectedCompatibleVehicles: List<DCompatibleVehicle> = emptyList(),
    /** VINs de los vehículos del garaje enlazados al repuesto. */
    val selectedGarageVins: List<String> = emptyList(),
    /** Catálogo de marcas/modelos de vehículo para la sección de compatibilidad. */
    val vehicleBrands: List<DVehicleBrand> = emptyList(),
    val isSaving: Boolean = false,
    val isDeleting: Boolean = false,
    /** Mensaje a mostrar en snackbar: sentinel de validación o mensaje crudo del backend. */
    val snackbarMessage: String? = null,
    /** True cuando el guardado ha tenido éxito (App.kt navega de vuelta). */
    val saved: Boolean = false,
    /** True cuando la eliminación ha tenido éxito (App.kt navega de vuelta). */
    val deleted: Boolean = false
)

/**
 * ViewModel para crear y editar un repuesto personal ([DMySparePart]).
 *
 * Gestiona los campos del formulario, las dos relaciones opcionales (vehículos
 * compatibles del catálogo y vehículos del garaje del usuario) y las acciones de
 * guardar/eliminar. En modo edición ([partId] != null) precarga el repuesto.
 */
class MySparePartEditViewModel(
    private val mySparePartsUseCase: IMySparePartsUseCase,
    private val catalogRepository: ICatalogRepository,
    private val sparePartCatalog: DSparePartCatalog,
    partId: Long? = null,
    /**
     * VIN a preseleccionar en la multi-selección de vehículos del garaje (solo modo creación).
     * Se usa al llegar desde el detalle de vehículo con "añadir repuesto" para este coche.
     */
    private val preselectVin: String? = null
) : ViewModel() {

    companion object {
        /** Sentinel de snackbar: faltan campos obligatorios. */
        const val SNACKBAR_REQUIRED_FIELDS = "required_fields"
        /** Sentinel de snackbar: valor de durabilidad no numérico. */
        const val SNACKBAR_INVALID_DURABILITY = "invalid_durability"
        /** Sentinel de snackbar: error genérico del backend sin mensaje. */
        const val SNACKBAR_GENERIC_ERROR = "generic_error"
    }

    private val _uiState = MutableStateFlow(
        MySparePartEditUiState(partId = partId, isLoading = partId != null)
    )
    val uiState: StateFlow<MySparePartEditUiState> = _uiState.asStateFlow()

    init {
        if (partId != null) {
            loadPart(partId)
        } else {
            preselectGarageVehicle(preselectVin)
        }
        loadVehicleCatalog()
    }

    /** En modo creación, deja el VIN indicado ya marcado en la selección del garaje. */
    private fun preselectGarageVehicle(vin: String?) {
        if (!vin.isNullOrBlank()) {
            _uiState.update { it.copy(selectedGarageVins = listOf(vin)) }
        }
    }

    // ------------------------------------------------------------------ campos

    fun onCategorySelected(categoryId: String?) {
        _uiState.update { it.copy(categoryId = categoryId, sparePartTypeId = null, snackbarMessage = null) }
    }

    fun onSparePartTypeSelected(sparePartTypeId: String?) {
        _uiState.update { it.copy(sparePartTypeId = sparePartTypeId, snackbarMessage = null) }
    }

    fun onBrandChange(brand: String) {
        _uiState.update { it.copy(brand = brand, snackbarMessage = null) }
    }

    fun onModelChange(model: String) {
        _uiState.update { it.copy(model = model, snackbarMessage = null) }
    }

    fun onReferenceChange(reference: String) {
        _uiState.update { it.copy(reference = reference, snackbarMessage = null) }
    }

    fun onDurabilityDaysChange(durabilityDays: String) {
        _uiState.update { it.copy(durabilityDays = durabilityDays, snackbarMessage = null) }
    }

    fun onAdditionalInfoChange(additionalInfo: String) {
        _uiState.update { it.copy(additionalInfo = additionalInfo, snackbarMessage = null) }
    }

    // ------------------------------------------------------- vehículos compatibles

    /**
     * Añade una pareja marca+modelo (o solo marca si [modelId] es null).
     * Las entradas duplicadas se ignoran.
     */
    fun addCompatibleVehicle(brandId: Int, modelId: Int?) {
        _uiState.update { state ->
            val entry = DCompatibleVehicle(brandId = brandId, modelId = modelId)
            if (state.selectedCompatibleVehicles.contains(entry)) state
            else state.copy(
                selectedCompatibleVehicles = state.selectedCompatibleVehicles + entry,
                snackbarMessage = null
            )
        }
    }

    fun removeCompatibleVehicle(vehicle: DCompatibleVehicle) {
        _uiState.update {
            it.copy(selectedCompatibleVehicles = it.selectedCompatibleVehicles - vehicle, snackbarMessage = null)
        }
    }

    // ---------------------------------------------------------- vehículos del garaje

    /** Marca un vehículo del garaje si no lo estaba (p. ej. el que asocia una nota). */
    fun selectGarageVehicle(vin: String) {
        _uiState.update { state ->
            if (vin in state.selectedGarageVins) state
            else state.copy(selectedGarageVins = state.selectedGarageVins + vin, snackbarMessage = null)
        }
    }

    /** Alterna un vehículo del garaje en la selección (multi-selección). */
    fun toggleGarageVehicle(vin: String) {
        _uiState.update { state ->
            val selected = if (vin in state.selectedGarageVins) {
                state.selectedGarageVins - vin
            } else {
                state.selectedGarageVins + vin
            }
            state.copy(selectedGarageVins = selected, snackbarMessage = null)
        }
    }

    // ------------------------------------------------------------------ guardar / eliminar

    /** Valida el formulario y crea o actualiza el repuesto según [MySparePartEditUiState.partId]. */
    fun save() {
        val state = _uiState.value
        if (state.isSaving || state.saved) return
        if (state.categoryId == null || state.sparePartTypeId == null ||
            state.brand.isBlank() || state.model.isBlank()
        ) {
            _uiState.update { it.copy(snackbarMessage = SNACKBAR_REQUIRED_FIELDS) }
            return
        }
        val durabilityDays = state.durabilityDays.trim().ifBlank { null }?.toIntOrNull()
        if (state.durabilityDays.isNotBlank() && durabilityDays == null) {
            _uiState.update { it.copy(snackbarMessage = SNACKBAR_INVALID_DURABILITY) }
            return
        }

        val part = DMySparePart(
            id = state.partId ?: 0L,
            categoryId = state.categoryId!!,
            sparePartId = state.sparePartTypeId!!,
            brand = state.brand.trim(),
            model = state.model.trim(),
            additionalInfo = state.additionalInfo.trim().ifBlank { null },
            durabilityDays = durabilityDays,
            reference = state.reference.trim().ifBlank { null },
            compatibleVehicles = state.selectedCompatibleVehicles,
            vehicleVins = state.selectedGarageVins
        )

        _uiState.update { it.copy(isSaving = true, snackbarMessage = null) }
        viewModelScope.launch {
            val result = if (state.partId != null) {
                mySparePartsUseCase.updateMySparePart(state.partId!!, part)
            } else {
                mySparePartsUseCase.createMySparePart(part)
            }
            when (result) {
                is DResponse.Success -> _uiState.update { it.copy(isSaving = false, saved = true) }
                else -> _uiState.update {
                    it.copy(
                        isSaving = false,
                        snackbarMessage = (result as? DResponse.Error)?.error
                            ?.takeIf { message -> message.isNotBlank() } ?: SNACKBAR_GENERIC_ERROR
                    )
                }
            }
        }
    }

    /** Elimina (soft delete) el repuesto. Solo tiene efecto en modo edición. */
    fun delete() {
        val state = _uiState.value
        val id = state.partId ?: return
        if (state.isDeleting || state.deleted) return
        _uiState.update { it.copy(isDeleting = true, snackbarMessage = null) }
        viewModelScope.launch {
            when (val result = mySparePartsUseCase.deleteMySparePart(id)) {
                is DResponse.Success -> _uiState.update { it.copy(isDeleting = false, deleted = true) }
                else -> _uiState.update {
                    it.copy(
                        isDeleting = false,
                        snackbarMessage = (result as? DResponse.Error)?.error
                            ?.takeIf { message -> message.isNotBlank() } ?: SNACKBAR_GENERIC_ERROR
                    )
                }
            }
        }
    }

    /**
     * Reinicia las banderas de éxito una vez que App.kt ha navegado de vuelta,
     * para que reabrir la ruta no dispare otra navegación automática.
     *
     * En modo creación también vacía el formulario: la factory mantiene este ViewModel toda
     * la sesión, y sin esto el siguiente "añadir repuesto" aparecería con los datos del anterior.
     */
    fun clearSavedFlags() {
        _uiState.update { state ->
            if (state.partId == null) {
                MySparePartEditUiState(
                    selectedGarageVins = listOfNotNull(preselectVin?.takeIf { it.isNotBlank() }),
                    vehicleBrands = state.vehicleBrands
                )
            } else {
                state.copy(saved = false, deleted = false)
            }
        }
    }

    // ------------------------------------------------------------------ carga inicial

    private fun loadPart(id: Long) {
        viewModelScope.launch {
            when (val result = mySparePartsUseCase.getMySparePart(id)) {
                is DResponse.Success -> _uiState.update {
                    it.copy(
                        isLoading = false,
                        categoryId = result.data.categoryId,
                        sparePartTypeId = result.data.sparePartId,
                        brand = result.data.brand,
                        model = result.data.model,
                        reference = result.data.reference ?: "",
                        durabilityDays = result.data.durabilityDays?.toString() ?: "",
                        additionalInfo = result.data.additionalInfo ?: "",
                        selectedCompatibleVehicles = result.data.compatibleVehicles,
                        selectedGarageVins = result.data.vehicleVins
                    )
                }
                else -> _uiState.update { it.copy(isLoading = false, loadError = (result as? DResponse.Error)?.error) }
            }
        }
    }

    private fun loadVehicleCatalog() {
        viewModelScope.launch {
            when (val result = catalogRepository.getCatalog()) {
                is DResponse.Success -> _uiState.update { it.copy(vehicleBrands = result.data.toDomainVehicleBrands()) }
                else -> Unit // Sin catálogo no se pueden añadir compatibilidades; guardar con la lista vacía sigue siendo posible.
            }
        }
    }
}

/**
 * Convierte el catálogo remoto de marcas/modelos a entidades de dominio,
 * para resolver entradas [DCompatibleVehicle] (brand_id/model_id) en nombres.
 */
fun CatalogResponseDto.toDomainVehicleBrands(): List<DVehicleBrand> = brands.map { brand ->
    DVehicleBrand(
        id = brand.id.toInt(),
        name = brand.name,
        models = brand.models.map { model ->
            DVehicleModel(id = model.id.toInt(), name = model.name)
        }
    )
}
